import { AuthLayout } from '@/components/auth'
import { Button, Control, PasswordInput, useForm, Icon } from '@tempots/beatui'
import { attr, html, on, Ensure, Unless, prop } from '@tempots/dom'
import { Validation } from '@tempots/std'
import { Anchor, NavigationService } from '@tempots/ui'
import { auth } from '@/services/gateway'
import { resetPasswordSchema } from '@/models/auth'
import { isApiError } from '@/services/errors'

export function ResetPasswordPage() {
  // Extract URL params into signal for reactivity
  const token = prop(
    new URLSearchParams(window.location.search).get('token') || ''
  )

  const { controller, submit, submitting } = useForm({
    initialValue: {
      password: '',
      confirmPassword: '',
    },
    schema: resetPasswordSchema,
    onSubmit: async (value) => {
      const tokenValue = token.value
      if (!tokenValue) {
        return Validation.invalid({ message: 'Invalid or missing reset token' })
      }
      try {
        await auth.resetPassword({
          token: tokenValue,
          password: value.password,
        })
        NavigationService.navigate('/signin?reset=success')
        return Validation.valid
      } catch (err) {
        const message = isApiError(err)
          ? (err.detail ?? err.title)
          : err instanceof Error
            ? err.message
            : 'Failed to reset password. The link may have expired.'
        return Validation.invalid({ message })
      }
    },
  })

  const password = controller.field('password')

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full space-y-8'),
      // Header
      html.div(
        attr.class('text-center'),
        html.h2(
          attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
          'Set new password'
        ),
        html.p(
          attr.class('mt-2 text-sm sm:text-base text-gray-600'),
          'Create a new password for your account'
        )
      ),
      // Error message display
      Ensure(controller.error, (error) =>
        html.div(
          attr.class(
            'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
          ),
          error
        )
      ),
      // Missing token warning
      Unless(
        token.map((t) => !!t),
        () =>
          html.div(
            attr.class(
              'bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg text-sm'
            ),
            'Invalid or missing reset token. Please request a new password reset link.'
          )
      ),
      // Form
      html.form(
        on.submit((e) => {
          e.preventDefault()
          submit(e)
        }),
        attr.class('space-y-6'),
        html.div(
          Control(PasswordInput, {
            controller: password,
            label: 'New Password',
          })
        ),
        html.div(
          Control(PasswordInput, {
            controller: controller.field('confirmPassword'),
            label: 'Confirm Password',
          })
        ),
        html.div(
          attr.class('flex items-center justify-center'),
          Button(
            {
              type: 'submit',
              fullWidth: true,
              loading: submitting,
              disabled: submitting,
            },
            submitting.map((s) => (s ? 'Resetting...' : 'Reset password'))
          )
        )
      ),
      // Back to sign in
      html.div(
        attr.class('text-center'),
        Anchor(
          { href: '/signin' },
          attr.class(
            'inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700'
          ),
          Icon({ icon: 'mdi:arrow-left' }, attr.class('w-4 h-4')),
          'Back to sign in'
        )
      )
    )
  )
}
